import {
  BirthControlMethodBreakpointOption,
  CycleChangesBreakpointOption,
  Experiment,
  HormonalBirthControlHowLongBreakpointOption,
  HormonalBirthControlUsageBreakpointOption,
  MedicalConditionsBreakpointOption,
  SideEffectsBreakpointOption,
} from '@naturalcycles/shared'
import { allQuizSections } from '@src/app/constants/quiz.js'
import { QuizSignatureObject } from '@src/app/core/services/quiz/quiz.service.js'
import { QuizModifier } from '@src/app/core/util/quiz.util.js'
import { AnyPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { Breakpoint, DefaultSections, Sections } from '@src/app/shared/typings/enum/quiz.js'
import { SignupQuizFlow, WSQuizEngineConfig } from '@src/app/shared/typings/interfaces/quiz.js'
import {
  getAb392DefaultChaptersBreakpointWrapper,
  getAb392GoalBreakpointAction,
  getAb392PlanConceiveTimingBreakpointAction,
} from './ab392.js'

class Exp392Quiz implements QuizModifier<WSQuizEngineConfig> {
  public _shouldEnhance(obj: QuizSignatureObject): boolean {
    return obj.experiment[Experiment.QUIZ_CHAPTERS_SANS_PROGRESS_BAR] === 'test'
  }

  public enhanceEngineConfig(
    obj: QuizSignatureObject,
    config: WSQuizEngineConfig,
  ): WSQuizEngineConfig {
    if (!this._shouldEnhance(obj)) return config
    // Goal takes care of chapter 1 (and 2 for Prevent)
    config.breakpointActions[Breakpoint.goal] = getAb392GoalBreakpointAction(
      config.breakpointActions[Breakpoint.goal],
    )

    // Chapter 2 and 3 for plan
    config.breakpointActions[Breakpoint.conceiveTiming] =
      getAb392PlanConceiveTimingBreakpointAction(
        config.breakpointActions[Breakpoint.conceiveTiming],
      )

    // Chapter 3 for prevent
    // Depending on flow, any of these could be the last breakpoint before cycle for prevent users
    config.breakpointActions[Breakpoint.hormonalBirthControlUsage] =
      getAb392DefaultChaptersBreakpointWrapper(
        config.breakpointActions[Breakpoint.hormonalBirthControlUsage],
        HormonalBirthControlUsageBreakpointOption,
        DefaultSections.cycle,
        Page.cycleChapter,
      )
    config.breakpointActions[Breakpoint.sideEffects] = getAb392DefaultChaptersBreakpointWrapper(
      config.breakpointActions[Breakpoint.sideEffects],
      SideEffectsBreakpointOption,
      DefaultSections.cycle,
      Page.cycleChapter,
    )
    config.breakpointActions[Breakpoint.birthControlMethod] =
      getAb392DefaultChaptersBreakpointWrapper(
        config.breakpointActions[Breakpoint.birthControlMethod],
        BirthControlMethodBreakpointOption,
        DefaultSections.cycle,
        Page.cycleChapter,
      )

    // Chapter 4
    config.breakpointActions[Breakpoint.hormonalBirthControlHowLong] =
      getAb392DefaultChaptersBreakpointWrapper(
        config.breakpointActions[Breakpoint.hormonalBirthControlHowLong],
        HormonalBirthControlHowLongBreakpointOption,
        DefaultSections.cycleChanges,
        Page.changesChapter,
      )
    config.breakpointActions[Breakpoint.medicalConditions] =
      getAb392DefaultChaptersBreakpointWrapper(
        config.breakpointActions[Breakpoint.medicalConditions],
        MedicalConditionsBreakpointOption,
        DefaultSections.cycleChanges,
        Page.changesChapter,
      )

    // Chapter 5
    config.breakpointActions[Breakpoint.cycleChanges] = getAb392DefaultChaptersBreakpointWrapper(
      config.breakpointActions[Breakpoint.cycleChanges],
      CycleChangesBreakpointOption,
      DefaultSections.routine,
      Page.routineChapter,
    )

    return config
  }

  public enhanceSections(obj: QuizSignatureObject, sections: Sections): Sections {
    if (!this._shouldEnhance(obj)) return sections
    return sections
  }

  public enhanceInitialFlow(obj: QuizSignatureObject, flow: SignupQuizFlow): SignupQuizFlow {
    if (!this._shouldEnhance(obj)) return flow

    const introSection = flow[allQuizSections.intro]

    if (!introSection) return flow
    if (introSection.includes(Page.introChapter)) return flow

    const namePageIndex = introSection.indexOf(Page.name)
    if (namePageIndex === -1) return flow

    introSection.splice(namePageIndex, 0, Page.introChapter)
    return flow
  }

  public enhanceEntry(obj: QuizSignatureObject, entry: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return entry
    return entry
  }

  public enhanceExit(obj: QuizSignatureObject, exit: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return exit
    return exit
  }
}

export const exp392Quiz = new Exp392Quiz()
